import { Controller } from '@hotwired/stimulus';
import Logger from '../lib/logger';
// Lists the headings of a long block of content, each a button that scrolls to its heading.
//
// The list is built from the content as it stands — nothing in it is written twice. It goes into the `list`
// target when there is one, and is otherwise made just before the first heading. A block with fewer than
// `minimum` headings gets none: two headings are found by looking. With a `back-label`, each heading listed gets
// a small button at its end that returns to the list — a long block is left by the way it was entered.
//
// A `ref` target is a button elsewhere in the block that leads to one of the headings listed, named by its place
// in the list (`data-toc-index-param`, from 0). It takes the heading's own words, so it says where it leads in the
// language the block is read in, and one added later — streamed in — is filled the same way.
//
//   <div data-controller="toc" data-toc-selector-value="h3" data-toc-label-value="Jump to a symptom"
//        data-toc-back-label-value="Back to the symptoms">
//     <button type="button" data-toc-target="ref" data-action="toc#jump" data-toc-index-param="4">step 5</button>
//     …
//   </div>
class default_1 extends Controller {
    constructor() {
        super(...arguments);
        this.made = null;
        this.nav = null;
        this.backs = [];
        this.headings = new WeakMap();
        // The headings listed, in the list's order, each with its title as the list gives it.
        this.listed = [];
    }
    connect() {
        Logger.debug('Controller connected', { controller: this.constructor.name });
        this.build();
    }
    disconnect() {
        Logger.debug('Controller disconnected', { controller: this.constructor.name });
        this.made?.remove();
        this.made = null;
        this.nav = null;
        this.backs.forEach((back) => back.remove());
        this.backs = [];
        this.listed = [];
    }
    // Targets connect before connect() builds the list, so the ones there from the start are filled by build().
    refTargetConnected(ref) {
        this.fill(ref);
    }
    back() {
        this.nav?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        this.nav?.querySelector('.toc-link')?.focus({ preventScroll: true });
    }
    jump(event) {
        const button = event.currentTarget;
        const heading = this.headings.get(button) ?? this.listed[Number(button.dataset.tocIndexParam)]?.heading;
        if (!heading)
            return;
        heading.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        // Focus follows, so a keyboard reader lands where the eye does; the heading is not otherwise focusable.
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
    }
    build() {
        const root = this.element;
        const headings = Array.from(root.querySelectorAll(this.selectorValue)).filter((heading) => !heading.closest('.toc'));
        if (headings.length < this.minimumValue)
            return;
        const nav = this.hasListTarget ? this.listTarget : this.makeBefore(headings[0]);
        this.nav = nav;
        nav.classList.add('toc');
        nav.setAttribute('aria-label', this.labelValue);
        const label = document.createElement('span');
        label.className = 'toc-label';
        label.textContent = this.labelValue;
        const list = document.createElement('ol');
        list.className = 'toc-list';
        headings.forEach((heading) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'toc-link';
            button.dataset.action = 'toc#jump';
            // The heading's own number leads its title ("3. One path is down…"); the list's own would say it twice.
            const title = (heading.textContent ?? '').trim();
            button.textContent = title;
            this.headings.set(button, heading);
            this.listed.push({ heading, title });
            const item = document.createElement('li');
            item.append(button);
            list.append(item);
        });
        nav.replaceChildren(label, list);
        if (this.backLabelValue)
            headings.forEach((heading) => this.addBack(heading));
        this.refTargets.forEach((ref) => this.fill(ref));
    }
    // A reference says where it leads in the heading's own words; one that names no heading listed keeps its own.
    fill(ref) {
        const title = this.listed[Number(ref.dataset.tocIndexParam)]?.title;
        if (title)
            ref.textContent = title;
    }
    // After the list is built, so a heading's title in it is the heading's own words.
    addBack(heading) {
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'toc-back';
        back.dataset.action = 'toc#back';
        back.title = this.backLabelValue;
        back.setAttribute('aria-label', this.backLabelValue);
        back.textContent = '↑';
        heading.append(back);
        this.backs.push(back);
    }
    makeBefore(heading) {
        const nav = document.createElement('nav');
        heading.before(nav);
        this.made = nav;
        return nav;
    }
}
default_1.targets = ['list', 'ref'];
default_1.values = {
    selector: { type: String, default: 'h2, h3' },
    label: { type: String, default: 'On this page' },
    backLabel: { type: String, default: '' },
    minimum: { type: Number, default: 3 }
};
export default default_1;
